import type { Job } from "@/data/types";
import type { JobBomSource } from "@/data/jobBomSelector";
import type { OrderingPacket, OrderingPacketStatus } from "@/data/orderingPacket/types";
import type { TrackingStatusTag, POLineItem, POStatus, POTrackingEntry, POTrackingPurpose, PurchaseOrder } from "@/data/VendorPOContext";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export type OrderingQueueFilter = "all" | "not-ordered" | "ordered" | "confirmed" | "delivered";
export type OrderingPacketLaneFilter = OrderingPacketStatus | "all" | "active" | "closed";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface OrderingPacketPageQuery {
  status: OrderingPacketLaneFilter;
  page: number;
  pageSize: number;
  search?: string;
  vendor?: string;
  refreshToken?: number;
}

export interface OrderingQueueJobPageQuery {
  filter: OrderingQueueFilter;
  page: number;
  pageSize: number;
  search?: string;
  refreshToken?: number;
}

export interface OrderingQueueVendorGroup {
  poId?: string;
  vendorName: string;
  vendorId: string;
  vendorEmail: string;
  orderingMethod: string;
  items: Array<{ sku: string; productName: string; quantity: number; unitCost: number; lineTotal?: number }>;
  subtotal: number;
  tax: number;
  totalCost: number;
  status: POStatus | "Not Ordered";
  poNumber?: string;
  submittedAt?: string;
  deliveredAt?: string;
  externalPoNumber?: string;
  trackingNumbers?: POTrackingEntry[];
  warehouseId?: string | null;
}

export interface OrderingQueueJobEntry {
  job: Job;
  vendorGroups: OrderingQueueVendorGroup[];
  bomSource: JobBomSource;
  pos: PurchaseOrder[];
}

export interface OrderingQueueSummary {
  activePackets: number;
  packetExceptions: number;
  closedPackets: number;
  totalPackets: number;
  packetVendors: string[];
  jobsInQueue: number;
  notOrderedJobs: number;
  orderedJobs: number;
  confirmedJobs: number;
  deliveredJobs: number;
}

type DbOrderingPacketRow = {
  packet_id?: string | null;
  job_id?: string | null;
  job_label?: string | null;
  customer_name?: string | null;
  intended_vendor?: string | null;
  order_type?: string | null;
  sent_by?: string | null;
  sent_at?: string | null;
  expected_delivery_date?: string | null;
  status?: string | null;
  line_count?: number | null;
  projected_total?: number | null;
  actual_total?: number | null;
  exception_count?: number | null;
  payload?: Record<string, unknown> | null;
};

type DbOrderingJobRow = {
  organization_id?: string | null;
  id?: string | null;
  job_number?: string | null;
  title?: string | null;
  customer_name?: string | null;
  current_stage?: string | null;
  permit_required?: boolean | null;
  material_readiness?: string | null;
  notes?: string | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  project_type?: string | null;
  metadata?: Record<string, unknown> | null;
  source_meta?: Job["sourceMeta"] | null;
  materials_list?: unknown[] | null;
  vendor_groups?: unknown[] | null;
  purchase_orders?: unknown[] | null;
  bom_source?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type DbOrderingSummaryRow = {
  active_packets?: number | null;
  packet_exceptions?: number | null;
  closed_packets?: number | null;
  total_packets?: number | null;
  packet_vendors?: string[] | null;
  jobs_in_queue?: number | null;
  not_ordered_jobs?: number | null;
  ordered_jobs?: number | null;
  confirmed_jobs?: number | null;
  delivered_jobs?: number | null;
};

const PACKET_COLUMNS = [
  "packet_id",
  "job_id",
  "job_label",
  "customer_name",
  "intended_vendor",
  "order_type",
  "sent_by",
  "sent_at",
  "expected_delivery_date",
  "status",
  "line_count",
  "projected_total",
  "actual_total",
  "exception_count",
  "payload",
].join(", ");

const JOB_COLUMNS = [
  "organization_id",
  "id",
  "job_number",
  "title",
  "customer_name",
  "current_stage",
  "permit_required",
  "material_readiness",
  "notes",
  "address_line1",
  "city",
  "state",
  "postal_code",
  "project_type",
  "metadata",
  "source_meta",
  "materials_list",
  "vendor_groups",
  "purchase_orders",
  "bom_source",
  "created_at",
  "updated_at",
].join(", ");

const SUMMARY_COLUMNS = [
  "active_packets",
  "packet_exceptions",
  "closed_packets",
  "total_packets",
  "packet_vendors",
  "jobs_in_queue",
  "not_ordered_jobs",
  "ordered_jobs",
  "confirmed_jobs",
  "delivered_jobs",
].join(", ");

function clampPage(query: { page: number; pageSize: number }) {
  const page = Math.max(0, Number(query.page) || 0);
  const pageSize = Math.max(1, Math.min(Number(query.pageSize) || 25, 100));
  return { page, pageSize, from: page * pageSize, to: page * pageSize + pageSize - 1 };
}

function sanitizeLike(value: string): string {
  return value.replace(/[%_]/g, "").replace(/,/g, " ").trim();
}

function applySearch(query: any, search: string | undefined, columns: string[]) {
  const term = sanitizeLike(String(search ?? ""));
  if (!term) return query;
  return query.or(columns.map((column) => `${column}.ilike.%${term}%`).join(","));
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown, fallback = ""): string {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function asBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  return ["true", "1", "yes"].includes(String(value ?? "").trim().toLowerCase());
}

function normalizePoStatus(value: unknown): POStatus {
  const status = String(value ?? "");
  return status === "Draft" || status === "Submitted" || status === "Confirmed" || status === "In Transit" || status === "Delivered" || status === "Received"
    ? status
    : "Draft";
}

function normalizeQueueStatus(value: unknown): POStatus | "Not Ordered" {
  const status = String(value ?? "");
  return status === "Not Ordered" ? "Not Ordered" : normalizePoStatus(status);
}

const VALID_TRACKING_TAGS = new Set<string>([
  "Pending", "InfoReceived", "InTransit", "OutForDelivery",
  "Delivered", "Exception", "Expired",
]);

function normalizeTrackingEntries(value: unknown): POTrackingEntry[] {
  const rows = Array.isArray(value) ? value : [];
  return rows
    .map((row, index) => {
      const entry = asRecord(row);
      const trackingNumber = asString(entry.trackingNumber ?? entry.tracking_number);
      if (!trackingNumber) return null;
      const rawTag = String(entry.trackingStatus ?? "").trim();
      return {
        id: asString(entry.id, `tracking-${index}`),
        trackingNumber,
        carrier: asString(entry.carrier) || undefined,
        purpose: asString(entry.purpose, "outbound") as POTrackingPurpose,
        notes: asString(entry.notes) || undefined,
        trackingStatus: VALID_TRACKING_TAGS.has(rawTag) ? rawTag as TrackingStatusTag : undefined,
        currentLocation: String(entry.currentLocation ?? "").trim() || undefined,
        estimatedDelivery: String(entry.estimatedDelivery ?? "").trim() || undefined,
        lastEventAt: String(entry.lastEventAt ?? "").trim() || undefined,
        lastEventDescription: String(entry.lastEventDescription ?? "").trim() || undefined,
      };
    })
    .filter((row): row is POTrackingEntry => Boolean(row));
}

function mapLineItem(value: unknown): POLineItem {
  const row = asRecord(value);
  const quantity = asNumber(row.quantity ?? row.quantityOrdered ?? row.quantity_ordered, 0);
  const unitCost = asNumber(row.unitCost ?? row.unit_cost, 0);
  return {
    sku: asString(row.sku),
    productName: asString(row.productName ?? row.product_name ?? row.itemName ?? row.sku, "Unmapped Product"),
    quantity,
    unitCost,
    lineTotal: asNumber(row.lineTotal ?? row.line_total, quantity * unitCost),
  };
}

function mapVendorGroup(value: unknown): OrderingQueueVendorGroup {
  const row = asRecord(value);
  return {
    poId: asString(row.poId) || undefined,
    vendorName: asString(row.vendorName, "Unassigned"),
    vendorId: asString(row.vendorId),
    vendorEmail: asString(row.vendorEmail),
    orderingMethod: asString(row.orderingMethod, "Email"),
    items: asArray(row.items).map(mapLineItem),
    subtotal: asNumber(row.subtotal ?? row.totalCost),
    tax: asNumber(row.tax),
    totalCost: asNumber(row.totalCost),
    status: normalizeQueueStatus(row.status),
    poNumber: asString(row.poNumber) || undefined,
    externalPoNumber: asString(row.externalPoNumber) || undefined,
    trackingNumbers: normalizeTrackingEntries(row.trackingNumbers),
    warehouseId: asString(row.warehouseId) || undefined,
  };
}

function mapPurchaseOrder(value: unknown): PurchaseOrder {
  const row = asRecord(value);
  const items = asArray(row.items).map(mapLineItem);
  return {
    poId: asString(row.poId),
    poNumber: asString(row.poNumber),
    externalPoNumber: asString(row.externalPoNumber) || undefined,
    jobId: asString(row.jobId),
    jobLabel: asString(row.jobLabel),
    vendorId: asString(row.vendorId),
    vendorName: asString(row.vendorName, "Unassigned"),
    items,
    status: normalizePoStatus(row.status),
    totalCost: asNumber(row.totalCost, items.reduce((sum, item) => sum + item.lineTotal, 0)),
    createdAt: asString(row.createdAt, new Date().toISOString()),
    submittedAt: asString(row.submittedAt) || undefined,
    confirmedAt: asString(row.confirmedAt) || undefined,
    deliveredAt: asString(row.deliveredAt) || undefined,
    trackingNumbers: normalizeTrackingEntries(row.trackingNumbers),
    notes: asString(row.notes),
    flagged: asBoolean(row.flagged),
    flagReason: asString(row.flagReason) || undefined,
    warehouseId: asString(row.warehouseId) || undefined,
  };
}

function mapMaterial(value: unknown): NonNullable<Job["materialsList"]>[number] {
  const row = asRecord(value);
  return {
    sku: asString(row.sku),
    itemName: asString(row.itemName ?? row.productName ?? row.sku, "Unmapped Product"),
    quantityRequired: asNumber(row.quantityRequired),
    quantityAllocated: asNumber(row.quantityAllocated),
    quantityStaged: asNumber(row.quantityStaged),
    quantityInstalled: asNumber(row.quantityInstalled),
    missingItems: asNumber(row.missingItems),
    fieldPurchase: asBoolean(row.fieldPurchase),
    unitCost: asNumber(row.unitCost),
  };
}

function mapJob(row: DbOrderingJobRow): Job {
  const metadata = row.metadata ?? {};
  const materialsList = asArray(row.materials_list).map(mapMaterial);
  return {
    id: asString(row.id),
    organizationId: asString(row.organization_id) || undefined,
    jobNumber: asString(row.job_number, asString(row.id)),
    customerName: asString(row.customer_name, "Unknown"),
    address: {
      street: asString(row.address_line1),
      city: asString(row.city),
      state: asString(row.state),
      zip: asString(row.postal_code),
    },
    projectType: asString(row.project_type ?? metadata.projectType, "Other") as Job["projectType"],
    assignedCrew: asString(metadata.assignedCrew),
    assignmentType: asString(metadata.assignmentType, "Unassigned") as Job["assignmentType"],
    scheduledInstallDate: asString(metadata.scheduledInstallDate),
    permitRequired: Boolean(row.permit_required),
    permitStatus: asString(metadata.permitStatus, "Not Required") as Job["permitStatus"],
    orderStatus: asString(metadata.orderStatus, "Not Ordered") as Job["orderStatus"],
    materialsStatus: asString(metadata.materialsStatus, "Not Ready") as Job["materialsStatus"],
    dispatchStatus: asString(metadata.dispatchStatus, "Not Ready") as Job["dispatchStatus"],
    contractorAssignmentStatus: asString(metadata.contractorAssignmentStatus),
    currentStage: asString(row.current_stage ?? metadata.currentStage, "Sale Closed") as Job["currentStage"],
    notes: asString(row.notes),
    description: asString(metadata.description ?? row.title),
    issueFlag: asBoolean(metadata.issueFlag),
    scopeSummary: asString(metadata.scopeSummary),
    payoutAmount: asNumber(metadata.payoutAmount),
    assignedSubcontractor: asString(metadata.assignedSubcontractor),
    targetInstallDate: asString(metadata.targetInstallDate),
    activityHistory: Array.isArray(metadata.activityHistory) ? metadata.activityHistory as Job["activityHistory"] : [],
    photos: Array.isArray(metadata.photos) ? metadata.photos as string[] : [],
    qaSubmitted: asBoolean(metadata.qaSubmitted),
    materialReadiness: asString(row.material_readiness, "Not Ordered") as Job["materialReadiness"],
    materialReadinessNotes: asString(metadata.materialReadinessNotes),
    missingItemsList: asString(metadata.missingItemsList),
    procurementStatus: asString(metadata.procurementStatus, "Not Ordered") as Job["procurementStatus"],
    warehouseStatus: asString(metadata.warehouseStatus, "Not Received") as Job["warehouseStatus"],
    finalDocumentationComplete: asBoolean(metadata.finalDocumentationComplete),
    customerSignoff: asBoolean(metadata.customerSignoff),
    closeoutNotes: asString(metadata.closeoutNotes),
    closeoutStatus: asString(metadata.closeoutStatus, "Not Started") as Job["closeoutStatus"],
    branch: asString(metadata.branch),
    region: asString(metadata.region),
    salesRep: asString(metadata.salesRep),
    phone: asString(metadata.phone),
    email: asString(metadata.email),
    secondaryContactName: asString(metadata.secondaryContactName),
    secondaryContactPhone: asString(metadata.secondaryContactPhone),
    secondaryContactEmail: asString(metadata.secondaryContactEmail),
    timeSlot: metadata.timeSlot as Job["timeSlot"],
    scheduledShifts: metadata.scheduledShifts as Job["scheduledShifts"],
    permitDetail: metadata.permitDetail as Job["permitDetail"],
    orderDetail: metadata.orderDetail as Job["orderDetail"],
    materialsList,
    financials: metadata.financials as Job["financials"],
    marketing: metadata.marketing as Job["marketing"],
    rescissionStatus: asString(metadata.rescissionStatus, "Cleared for Production") as Job["rescissionStatus"],
    rescissionEndsAt: metadata.rescissionEndsAt as Job["rescissionEndsAt"],
    projectConfig: metadata.projectConfig as Job["projectConfig"],
    externalRef: metadata.externalRef as Job["externalRef"],
    origin: metadata.origin as Job["origin"],
    scheduledStartTime: metadata.scheduledStartTime as string | undefined,
    scheduledEndTime: metadata.scheduledEndTime as string | undefined,
    timezone: metadata.timezone as string | undefined,
    externalCalendarEventId: metadata.externalCalendarEventId as string | undefined,
    boundJobSpec: (metadata.boundJobSpec ?? metadata.bound_job_spec) as Job["boundJobSpec"],
    executionReady: metadata.executionReady as Job["executionReady"],
    contractDocument: metadata.contractDocument as Job["contractDocument"],
    changeOrders: metadata.changeOrders as Job["changeOrders"],
    scopeLocked: metadata.scopeLocked as Job["scopeLocked"],
    opportunityId: metadata.opportunityId as Job["opportunityId"],
    appointmentResultId: metadata.appointmentResultId as Job["appointmentResultId"],
    intakeApproved: metadata.intakeApproved as Job["intakeApproved"],
    approvedForOrdering: metadata.approvedForOrdering as Job["approvedForOrdering"],
    holdStatus: metadata.holdStatus as Job["holdStatus"],
    holdReason: metadata.holdReason as Job["holdReason"],
    planningStatus: metadata.planningStatus as Job["planningStatus"],
    orderType: metadata.orderType as Job["orderType"],
    selectedVendor: metadata.selectedVendor as Job["selectedVendor"],
    selectedVendorType: metadata.selectedVendorType as Job["selectedVendorType"],
    schedulingStatus: metadata.schedulingStatus as Job["schedulingStatus"],
    installReadiness: metadata.installReadiness as Job["installReadiness"],
    jobMaterialStatus: metadata.jobMaterialStatus as Job["jobMaterialStatus"],
    installStatus: metadata.installStatus as Job["installStatus"],
    qaStatus: metadata.qaStatus as Job["qaStatus"],
    reviewSent: metadata.reviewSent as Job["reviewSent"],
    reviewCompleted: metadata.reviewCompleted as Job["reviewCompleted"],
    reviewSatisfied: metadata.reviewSatisfied as Job["reviewSatisfied"],
    reviewSentAt: metadata.reviewSentAt as Job["reviewSentAt"],
    referralRequested: metadata.referralRequested as Job["referralRequested"],
    referralReceived: metadata.referralReceived as Job["referralReceived"],
    createdAt: row.created_at ?? undefined,
    updatedAt: row.updated_at ?? undefined,
    sourceMeta: row.source_meta ?? undefined,
  };
}

function mapOrderingJob(row: DbOrderingJobRow): OrderingQueueJobEntry {
  const bomSource = String(row.bom_source ?? "none");
  return {
    job: mapJob(row),
    vendorGroups: asArray(row.vendor_groups).map(mapVendorGroup),
    bomSource: (bomSource === "jobspec" || bomSource === "materialsList" || bomSource === "inferred" ? bomSource : "none") as JobBomSource,
    pos: asArray(row.purchase_orders).map(mapPurchaseOrder),
  };
}

function mapOrderingPacket(row: DbOrderingPacketRow): OrderingPacket {
  const payload = asRecord(row.payload);
  return {
    ...(payload as Partial<OrderingPacket>),
    id: asString(payload.id, asString(row.packet_id)),
    jobId: asString(payload.jobId, asString(row.job_id)),
    jobLabel: asString(payload.jobLabel, asString(row.job_label, asString(row.job_id))),
    customerName: asString(payload.customerName, asString(row.customer_name, "Unknown")),
    intendedVendor: asString(payload.intendedVendor, asString(row.intended_vendor, "Unassigned")),
    orderType: asString(payload.orderType, asString(row.order_type, "Direct")) as OrderingPacket["orderType"],
    notes: asString(payload.notes),
    sentBy: asString(payload.sentBy, asString(row.sent_by)),
    sentAt: asString(payload.sentAt, asString(row.sent_at, new Date().toISOString())),
    status: asString(payload.status, asString(row.status, "draft")) as OrderingPacketStatus,
    expectedDeliveryDate: asString(payload.expectedDeliveryDate, asString(row.expected_delivery_date)) || undefined,
    lineItems: Array.isArray(payload.lineItems) ? payload.lineItems as OrderingPacket["lineItems"] : [],
    purchaseOrders: Array.isArray(payload.purchaseOrders) ? payload.purchaseOrders as OrderingPacket["purchaseOrders"] : [],
    receipts: Array.isArray(payload.receipts) ? payload.receipts as OrderingPacket["receipts"] : [],
    projectedTotal: asNumber(payload.projectedTotal, asNumber(row.projected_total)),
    actualTotal: asNumber(payload.actualTotal, asNumber(row.actual_total)),
    auditEvents: Array.isArray(payload.auditEvents) ? payload.auditEvents as OrderingPacket["auditEvents"] : [],
    exceptionCount: asNumber(payload.exceptionCount, asNumber(row.exception_count)),
  } as OrderingPacket;
}

function mapSummary(row: DbOrderingSummaryRow | null | undefined): OrderingQueueSummary {
  return {
    activePackets: Number(row?.active_packets ?? 0),
    packetExceptions: Number(row?.packet_exceptions ?? 0),
    closedPackets: Number(row?.closed_packets ?? 0),
    totalPackets: Number(row?.total_packets ?? 0),
    packetVendors: Array.isArray(row?.packet_vendors) ? row!.packet_vendors!.filter(Boolean).sort() : [],
    jobsInQueue: Number(row?.jobs_in_queue ?? 0),
    notOrderedJobs: Number(row?.not_ordered_jobs ?? 0),
    orderedJobs: Number(row?.ordered_jobs ?? 0),
    confirmedJobs: Number(row?.confirmed_jobs ?? 0),
    deliveredJobs: Number(row?.delivered_jobs ?? 0),
  };
}

export const OrderingQueuePageService = {
  async getPacketsPage(query: OrderingPacketPageQuery): Promise<PageResponse<OrderingPacket>> {
    const { from, to } = clampPage(query);
    const key = `ordering.packets.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        let request = supabase
          .from("v_ordering_packets_ui")
          .select(PACKET_COLUMNS, { count: "exact" })
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.status === "active") request = request.eq("is_active", true);
        else if (query.status === "closed") request = request.eq("is_closed", true);
        else if (query.status !== "all") request = request.eq("status", query.status);
        if (query.vendor && query.vendor !== "all") request = request.eq("intended_vendor", query.vendor);
        request = applySearch(request, query.search, ["packet_id", "job_label", "customer_name", "intended_vendor"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbOrderingPacketRow[]).map(mapOrderingPacket), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("OrderingQueuePageService.getPacketsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getJobsPage(query: OrderingQueueJobPageQuery): Promise<PageResponse<OrderingQueueJobEntry>> {
    const { from, to } = clampPage(query);
    const key = `ordering.jobs.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        let request = supabase
          .from("v_ordering_queue_jobs_ui")
          .select(JOB_COLUMNS, { count: "exact" })
          .eq("is_queue_eligible", true)
          .order("sort_at", { ascending: false, nullsFirst: false })
          .range(from, to);
        if (query.filter === "not-ordered") request = request.eq("has_not_ordered", true);
        if (query.filter === "ordered") request = request.eq("has_submitted", true);
        if (query.filter === "confirmed") request = request.eq("has_confirmed", true);
        if (query.filter === "delivered") request = request.eq("all_delivered", true);
        request = applySearch(request, query.search, ["job_number", "customer_name", "title", "project_type"]);
        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as DbOrderingJobRow[]).map(mapOrderingJob), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("OrderingQueuePageService.getJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: OrderingQueueSummary; error: unknown | null }> {
    const key = `ordering.summary.${refreshToken ?? 0}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_ordering_queue_summary")
          .select(SUMMARY_COLUMNS)
          .maybeSingle();
        if (error) throw error;
        return { data: mapSummary(data as DbOrderingSummaryRow | null), error: null };
      });
    } catch (error) {
      console.warn("OrderingQueuePageService.getSummary failed:", error);
      return { data: mapSummary(null), error };
    }
  },
};
